import { subDays } from 'date-fns';
import React, { FC } from 'react';
import { useWatch } from 'react-hook-form';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { FormDatePicker } from '../../../../../components/date-picker/index';
import { FormInput } from '../../../../../components/input/index';
import { useFormat } from '../../../../../hooks/index';
import { useCompanyIncorporationContext } from '../../../hooks/use-company-incorporation-context.hook';
import { ChildrenFormProps } from '../../event-form.types';
import { companyIncorporationStepOneSchema, FormSchema } from '../../event-form.validation';

export type CompanyIncorporationStepOneProps = ChildrenFormProps;

export const CompanyIncorporationStepOne: FC<CompanyIncorporationStepOneProps> = ({
  control,
  setFormData,
  handleCloseModal,
  nextFormStep,
  prevFormStep,
  lockMode,
  isLoading,
  companyId,
}) => {
  const { format } = useFormat();
  const { companyIncorporation } = useWatch<FormSchema>({ control });
  const { data: incorporationContext } = useCompanyIncorporationContext(companyId);

  const earliestExistingEventDate = incorporationContext?.earliestExistingEventDate
    ? new Date(incorporationContext.earliestExistingEventDate)
    : undefined;

  const isValid = companyIncorporationStepOneSchema.safeParse(companyIncorporation).success;

  const latestAllowedDate = earliestExistingEventDate
    ? subDays(earliestExistingEventDate, 1)
    : undefined;

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-6 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <FormDatePicker
          calendar={
            latestAllowedDate
              ? {
                  disabled: { after: latestAllowedDate },
                  toDate: latestAllowedDate,
                }
              : undefined
          }
          iconColor="#98A2B3"
          inputValue={
            companyIncorporation?.incorporationDate
              ? format(companyIncorporation.incorporationDate, 'dd/MM/yyyy')
              : ''
          }
          inputWrapperClassName="w-full"
          onSelect={(date) => {
            setFormData('companyIncorporation.incorporationDate', date, { shouldValidate: true });
          }}
          placeholder={<AppFormattedMessage id="INCORPORATION_DATE" />}
          value={companyIncorporation?.incorporationDate}
        />
        <FormInput
          control={control}
          currencySign
          maxDecimalPlaces={7}
          name="companyIncorporation.parValue"
          placeholder={<AppFormattedMessage id="PAR_VALUE" />}
          wrapperClassName="w-full"
        />
      </div>
      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          {!lockMode && (
            <Button
              className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
              onClick={prevFormStep}
              styleType="NONE"
              type="button"
            >
              <AppFormattedMessage id="BACK" />
            </Button>
          )}
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            disabled={!isValid}
            isLoading={isLoading}
            onClick={nextFormStep}
            type="button"
          >
            {lockMode ? <AppFormattedMessage id="UPDATE" /> : <AppFormattedMessage id="NEXT" />}
          </Button>
        </div>
      </div>
    </>
  );
};
